import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Upload, Building2, Mail, MapPin, Save, FileText } from 'lucide-react'
import toast from 'react-hot-toast'
import { useSettingsStore } from '@/store/settings.store'
import { showSaveSuccess, showSaveError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { branchService } from '@/services/branchService'
import { CountryLookup, CityLookup } from '@/api/types'

// Validation schema
const branchSchema = z.object({
  NameEn: z.string().min(1, 'Branch name (English) is required'),
  NameAr: z.string().optional(),
  Email: z.string().email('Invalid email address').optional().or(z.literal('')),
  Phone: z.string().optional(),
  Website: z.string().optional(),
  AddressEn: z.string().optional(),
  AddressAr: z.string().optional(),
  CrNo: z.string().optional(),
  VatNo: z.string().optional(),
  Language: z.string().optional(),
  Currency: z.string().optional(),
  BankDetailEn: z.string().optional(),
  BankDetailAr: z.string().optional(),
  FiscalYearStartMonth: z.number().optional(),
  FiscalYearIsBasedOn: z.string().optional(),
  IsEnable: z.boolean().optional(),
  ShortName: z.string().optional(),
  Country: z.number().optional(),
  City: z.number().optional(),
  ZipCode: z.string().optional(),
  Street: z.string().optional(),
  BuildingNumber: z.number().optional(),
  Plot: z.number().optional(),
  CitySubdivision: z.string().optional(),
  CountrySubentity: z.number().optional(),
})

type BranchFormData = z.infer<typeof branchSchema>

/**
 * Branch Edit/Create Page
 * Form for creating new branches or editing existing ones
 */
export const BranchesEdit: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const { language = 'en' } = useSettingsStore()
  
  const isEditing = Boolean(id)

  const [loading, setLoading] = useState(false)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [selectedImage, setSelectedImage] = useState<File | null>(null)

  // Lookup data
  const [countries, setCountries] = useState<CountryLookup[]>([])
  const [cities, setCities] = useState<CityLookup[]>([])

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<BranchFormData>({
    resolver: zodResolver(branchSchema),
    defaultValues: {
      IsEnable: true,
    },
  })

  const selectedCountryId = watch('Country')

  // Load lookup data
  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [countriesData] = await Promise.all([
          branchService.getCountryLookups(),
        ])

        setCountries(countriesData || [])
      } catch (error) {
        console.error('Error loading lookups:', error)
        toast.error('Failed to load form data')
      }
    }

    loadLookups()
  }, [])

  // Load cities when country changes
  useEffect(() => {
    const loadCities = async () => {
      if (selectedCountryId) {
        try {
          const citiesData = await branchService.getCityLookups(selectedCountryId)
          setCities(citiesData || [])
        } catch (error) {
          console.error('Error loading cities:', error)
        }
      } else {
        setCities([])
      }
    }

    loadCities()
  }, [selectedCountryId])

  // Load branch data for editing
  useEffect(() => {
    if (isEditing && id) {
      const loadBranch = async () => {
        try {
          setLoading(true)
          const branch = await branchService.getBranchById(Number(id))
          
          console.log('Loaded branch data:', branch)
          
          // Map API fields to form fields
          if (branch.nameEn) setValue('NameEn', branch.nameEn)
          if (branch.nameAr) setValue('NameAr', branch.nameAr)
          if (branch.email) setValue('Email', branch.email)
          if (branch.phone) setValue('Phone', branch.phone)
          if (branch.website) setValue('Website', branch.website)
          if (branch.addressEn) setValue('AddressEn', branch.addressEn)
          if (branch.addressAr) setValue('AddressAr', branch.addressAr)
          if (branch.crNo) setValue('CrNo', branch.crNo)
          if (branch.vatNo) setValue('VatNo', branch.vatNo)
          if (branch.language) setValue('Language', branch.language)
          if (branch.currency) setValue('Currency', branch.currency)
          if (branch.bankDetailEn) setValue('BankDetailEn', branch.bankDetailEn)
          if (branch.bankDetailAr) setValue('BankDetailAr', branch.bankDetailAr)
          if (branch.fiscalYearStartMonth) setValue('FiscalYearStartMonth', branch.fiscalYearStartMonth)
          if (branch.fiscalYearIsBasedOn) setValue('FiscalYearIsBasedOn', branch.fiscalYearIsBasedOn)
          if (branch.isEnable !== undefined) setValue('IsEnable', branch.isEnable)
          if (branch.shortName) setValue('ShortName', branch.shortName)
          if (branch.country) setValue('Country', branch.country)
          if (branch.city) setValue('City', branch.city)
          if (branch.zipCode) setValue('ZipCode', branch.zipCode)
          if (branch.street) setValue('Street', branch.street)
          if (branch.buildingNumber) setValue('BuildingNumber', branch.buildingNumber)
          if (branch.plot) setValue('Plot', branch.plot)
          if (branch.citySubdivision) setValue('CitySubdivision', branch.citySubdivision)
          if (branch.countrySubentity) setValue('CountrySubentity', typeof branch.countrySubentity === 'number' ? branch.countrySubentity : Number(branch.countrySubentity) || 0)

          // Set image preview if exists
          if (branch.logo || branch.logoUrl) {
            setImagePreview(branch.logo || branch.logoUrl || null)
          }
        } catch (error: any) {
          console.error('Error loading branch:', error)
          toast.error(error.response?.data?.message || 'Failed to load branch data')
          navigate('/branches')
        } finally {
          setLoading(false)
        }
      }

      loadBranch()
    }
  }, [isEditing, id, setValue, navigate])

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedImage(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const onSubmit = async (data: BranchFormData) => {
    try {
      setLoading(true)

      // Create FormData for multipart/form-data
      const formData = new FormData()

      // Add all form fields
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          formData.append(key, String(value))
        }
      })

      // Add company ID if available
      if (user?.userId) {
        formData.append('CompanyId', String(user.userId))
        formData.append('CreatedBy', String(user.userId))
        formData.append('ModifiedBy', String(user.userId))
      }

      // Add logo file if selected
      if (selectedImage) {
        formData.append('LogoFile', selectedImage)
      }

      // Add ID for update
      if (isEditing && id) {
        formData.append('Id', id)
      }

      let response
      if (isEditing) {
        response = await branchService.updateBranch(formData)
      } else {
        response = await branchService.saveBranch(formData)
      }

      // Handle response: API returns 200 OK on success
      // If response exists and doesn't explicitly have success: false, treat as success
      if (response && response.success !== false) {
        await showSaveSuccess(isEditing, isEditing ? 'Branch updated successfully' : 'Branch saved successfully')
        navigate('/branches')
      } else {
        await showSaveError(new Error(response?.message || 'Failed to save branch'), isEditing)
      }
    } catch (error: any) {
      console.error('Error saving branch:', error)
      await showSaveError(error, isEditing)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        title={isEditing ? 'Edit Branch' : 'Add New Branch'}
        description={isEditing ? 'Update branch information' : 'Add a new branch to your organization'}
      />

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-6">
          {/* Branch Logo */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Branch Logo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6">
                <div className="h-32 w-32 border-2 border-dashed border-muted-foreground/25 rounded-lg flex items-center justify-center overflow-hidden bg-muted/10">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Branch Logo" className="h-full w-full object-cover" />
                  ) : (
                    <Upload className="h-8 w-8 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1">
                  <Label htmlFor="logoFile">Upload Branch Logo</Label>
                  <Input
                    id="logoFile"
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="mt-2"
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    Select an image file for the branch logo
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Branch Info */}
          <Card>
            <CardHeader>
              <CardTitle>Branch Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="NameEn">
                    Branch Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="NameEn"
                    {...register('NameEn')}
                    className={errors.NameEn ? 'border-destructive' : ''}
                  />
                  {errors.NameEn && (
                    <p className="text-sm text-destructive mt-1">{errors.NameEn.message}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="NameAr">Branch Name in Arabic</Label>
                  <Input id="NameAr" {...register('NameAr')} />
                </div>
                <div>
                  <Label htmlFor="CrNo">CR Number</Label>
                  <Input id="CrNo" {...register('CrNo')} />
                </div>
                <div>
                  <Label htmlFor="VatNo">VAT Number</Label>
                  <Input id="VatNo" {...register('VatNo')} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Contact Info
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="Email">Email</Label>
                  <Input
                    id="Email"
                    type="email"
                    {...register('Email')}
                    className={errors.Email ? 'border-destructive' : ''}
                  />
                  {errors.Email && (
                    <p className="text-sm text-destructive mt-1">{errors.Email.message}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="Phone">Phone</Label>
                  <Input id="Phone" {...register('Phone')} />
                </div>
                <div>
                  <Label htmlFor="Website">Website</Label>
                  <Input id="Website" {...register('Website')} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Address */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Address
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="Street">Street</Label>
                  <Input id="Street" {...register('Street')} />
                </div>
                <div>
                  <Label htmlFor="BuildingNumber">Building Number</Label>
                  <Input
                    id="BuildingNumber"
                    type="number"
                    {...register('BuildingNumber', { valueAsNumber: true })}
                  />
                </div>
                <div>
                  <Label htmlFor="Plot">Additional Number</Label>
                  <Input
                    id="Plot"
                    type="number"
                    {...register('Plot', { valueAsNumber: true })}
                  />
                </div>
                <div>
                  <Label htmlFor="CitySubdivision">City Subdivision</Label>
                  <Input id="CitySubdivision" {...register('CitySubdivision')} />
                </div>
                <div>
                  <SearchableSelect
                    label="Country"
                    options={(countries || []).map((country) => ({
                      value: String(country.id),
                      label: language === 'ar' ? (country.lookupNameAr || country.lookupNameEn || '') : (country.lookupNameEn || country.lookupNameAr || ''),
                    }))}
                    value={watch('Country')?.toString() || ''}
                    onValueChange={(value) => setValue('Country', Number(value))}
                    placeholder="Select country"
                    searchPlaceholder="Search country..."
                    emptyMessage="No country found"
                  />
                </div>
                <div>
                  <SearchableSelect
                    label="City"
                    options={(cities || []).map((city) => ({
                      value: String(city.id),
                      label: language === 'ar' ? (city.lookupNameAr || city.lookupNameEn || '') : (city.lookupNameEn || city.lookupNameAr || ''),
                    }))}
                    value={watch('City')?.toString() || ''}
                    onValueChange={(value) => setValue('City', Number(value))}
                    placeholder="Select city"
                    searchPlaceholder="Search city..."
                    emptyMessage="No city found"
                    disabled={!selectedCountryId}
                  />
                </div>
                <div>
                  <Label htmlFor="ZipCode">Zip Code</Label>
                  <Input id="ZipCode" {...register('ZipCode')} />
                </div>
                <div>
                  <SearchableSelect
                    label="State"
                    options={(cities || []).map((city) => ({
                      value: String(city.id),
                      label: language === 'ar' ? (city.lookupNameAr || city.lookupNameEn || '') : (city.lookupNameEn || city.lookupNameAr || ''),
                    }))}
                    value={watch('CountrySubentity')?.toString() || ''}
                    onValueChange={(value) => setValue('CountrySubentity', Number(value))}
                    placeholder="Select state"
                    searchPlaceholder="Search state..."
                    emptyMessage="No state found"
                    disabled={!selectedCountryId}
                  />
                </div>
              </div>
              {/* Address Fields - Side by Side */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="AddressEn">Address</Label>
                  <Textarea id="AddressEn" {...register('AddressEn')} rows={3} />
                </div>
                <div>
                  <Label htmlFor="AddressAr">Address in Arabic</Label>
                  <Textarea id="AddressAr" {...register('AddressAr')} rows={3} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bank Detail */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Bank Detail
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="BankDetailEn">Bank Detail in English</Label>
                  <Textarea id="BankDetailEn" {...register('BankDetailEn')} rows={4} />
                </div>
                <div>
                  <Label htmlFor="BankDetailAr">Bank Detail in Arabic</Label>
                  <Textarea id="BankDetailAr" {...register('BankDetailAr')} rows={4} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/branches')} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="w-full sm:w-auto">
              {loading ? (
                <>
                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isEditing ? 'Update Branch' : 'Save Branch'}
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}

